import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import { Button, Tag, ToastContainer } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import {
  detectTextFileEncoding,
  getUtf8ErrorMessage,
} from "../../utils/fileTransfer";
import "./productManagement.scss";

const KeywordGroupEditModal = ({ isOpen, keywordGroup, onClose, onSave }) => {
  const [groupName, setGroupName] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [modalToast, setModalToast] = useState([]);
  const [showAllKeywords, setShowAllKeywords] = useState(false);

  const MAX_VISIBLE = 20;

  useEffect(() => {
    if (isOpen) {
      if (keywordGroup) {
        setGroupName(keywordGroup.name || "");
        setKeywords([...(keywordGroup.keywords || [])]);
      } else {
        setGroupName("");
        setKeywords([]);
      }
      setNewKeyword("");
      setModalToast([]);
      setShowAllKeywords(false);
    }
  }, [isOpen, keywordGroup]);

  const handleClose = useCallback(() => {
    setGroupName("");
    setKeywords([]);
    setNewKeyword("");
    setModalToast([]);
    onClose();
  }, [onClose]);

  const handleAddKeyword = useCallback(() => {
    const trimmed = newKeyword.trim();
    if (!trimmed) return;
    if (keywords.includes(trimmed)) {
      setModalToast([
        { text: "Keyword already exists", type: "error", id: "dup-keyword" },
      ]);
      return;
    }
    setKeywords((prev) => [...prev, trimmed]);
    setNewKeyword("");
    setModalToast([]);
  }, [newKeyword, keywords]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddKeyword();
      }
    },
    [handleAddKeyword]
  );

  const handleRemoveKeyword = useCallback((keyword) => {
    setKeywords((prev) => prev.filter((k) => k !== keyword));
  }, []);

  const handleImportKeywords = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.txt";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const encoding = await detectTextFileEncoding(file);
        if (encoding !== "UTF-8") {
          setModalToast([
            {
              text: getUtf8ErrorMessage(file.name, encoding),
              type: "error",
              id: "encoding-error",
            },
          ]);
          return;
        }
        const text = await file.text();
        const imported = text
          .split(/[\n,;]+/)
          .map((k) => k.trim())
          .filter((k) => k.length > 0);
        const unique = [...new Set([...keywords, ...imported])];
        setKeywords(unique);
        setModalToast([
          {
            text: `${imported.length} keywords imported`,
            type: "success",
            id: "import-success",
          },
        ]);
      } catch {
        setModalToast([
          { text: "Failed to read file", type: "error", id: "import-error" },
        ]);
      }
    };
    input.click();
  }, [keywords]);

  const handleExportKeywords = useCallback(() => {
    const blob = new Blob([keywords.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${groupName || "keywords"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [keywords, groupName]);

  const handleSave = useCallback(() => {
    const name = groupName.trim();
    if (!name) {
      setModalToast([
        { text: "Group name is required", type: "error", id: "name-error" },
      ]);
      return;
    }
    if (keywords.length === 0) {
      setModalToast([
        { text: "At least one keyword is required", type: "error", id: "kw-error" },
      ]);
      return;
    }

    onSave({
      ...(keywordGroup || {}),
      name,
      keywords,
      tempId: keywordGroup?.tempId || `temp-${Date.now()}`,
    });
  }, [groupName, keywords, keywordGroup, onSave]);

  if (!isOpen) return null;

  const visibleKeywords = showAllKeywords
    ? keywords
    : keywords.slice(0, MAX_VISIBLE);
  const hasMore = keywords.length > MAX_VISIBLE;

  return (
    <Dialog
      className="keyword-group-edit-modal"
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <h3 style={{ margin: 0 }}>
              {keywordGroup ? "Edit Keyword Group" : "Add Keyword Group"}
            </h3>
          </Grid>
          <Grid item>
            <CloseIcon onClick={handleClose} className="cursor-pointer" />
          </Grid>
        </Grid>
        <ToastContainer
          data={modalToast}
          removeToast={() => setModalToast([])}
          showActionIcon
        />
      </DialogTitle>
      <DialogContent>
        <div className="keyword-group-field">
          <TextField
            fullWidth
            size="small"
            label="Group name"
            placeholder="Enter group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
        </div>

        <div className="keyword-input-row">
          <TextField
            className="keyword-input"
            size="small"
            label="Add keywords"
            placeholder="Enter keyword and press Enter"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={handleKeyDown}
            required
          />
          <div className="keyword-actions">
            <Button
              text="Export keywords"
              variant="secondary"
              size="small"
              onClick={handleExportKeywords}
              disabled={keywords.length === 0}
            />
            <Button
              text="Import keywords"
              variant="secondary"
              size="small"
              onClick={handleImportKeywords}
            />
          </div>
        </div>

        {keywords.length > 0 && (
          <div className="current-keywords-section">
            <div className="current-keywords-label">
              Current keywords ({keywords.length})
            </div>
            <div
              className={`keywords-tags-wrap ${!showAllKeywords ? "collapsed" : ""}`}
            >
              {visibleKeywords.map((keyword, index) => (
                <Tag
                  key={`${keyword}-${index}`}
                  text={keyword}
                  color="Gray-100"
                  isCopyable={false}
                  isRemovable={true}
                  onRemove={() => handleRemoveKeyword(keyword)}
                  size="small"
                />
              ))}
            </div>
            {hasMore && (
              <Button
                text={
                  showAllKeywords ? "Show less" : `Show all (${keywords.length})`
                }
                variant="tertiary"
                size="small"
                onClick={() => setShowAllKeywords(!showAllKeywords)}
                style={{ marginTop: 8 }}
              />
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          onClick={handleClose}
          text="Cancel"
          size="small"
        />
        <Button variant="primary" onClick={handleSave} text="Save" size="small" />
      </DialogActions>
    </Dialog>
  );
};

KeywordGroupEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  keywordGroup: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    keywords: PropTypes.array,
    isExisting: PropTypes.bool,
    tempId: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default KeywordGroupEditModal;
